import { Dispatch, Fragment, FunctionComponent, SetStateAction, useEffect, useState } from "react";
import { t } from "i18next";

import { classNames } from "primereact/utils";

import { sharedDialog } from "../../../components/Shared/Components/Dialogs/SharedDialog";
import { TooltipWrapper } from "../../../components/Shared/Components/TooltipWrapper/TooltipWrapper";
import { translationKeys } from "../../../models/app-translation-keys";

import "./ImageBackgroundControl.scss";

import { ImageBackgroundDialogContent } from "./ImageBackgroundDialogContent";
import { ImageBackgroundDialogFooter } from "./ImageBackgroundDialogFooter";
import { IBackgroundDialogState } from "./imageBackgroundDialogTypes";

export interface IChangeBackgroundPayload {
  backgroundColor?: string;
}

interface IImageBackgroundControlProps {
  backgroundColor?: string;
  disabled: boolean;
  onChange: (backgroundColor?: string) => void;
  strictIsolationGuaranteed: boolean;
}

const defaultPresets = ["#FFFFFF", "#000000", "#d9e3e8", "#e4ebf0", "#eff4f7", "#e9e4df", "#f0ece8", "#f6f4f0"];

const featureTranslationKeys = translationKeys.feature.imageGeneration;
const backgroundControlTranslationKeys = featureTranslationKeys.backgroundControl;

export const ImageBackgroundControl: FunctionComponent<IImageBackgroundControlProps> = ({
  backgroundColor,
  disabled,
  onChange,
  strictIsolationGuaranteed,
}) => {
  const [backgroundColorEnabled, setBackgroundColorEnabled] = useState<boolean>(!!backgroundColor);

  useEffect(() => {
    setBackgroundColorEnabled(!!backgroundColor);
  }, [backgroundColor]);

  const setBackgroundColor: Dispatch<SetStateAction<string | undefined>> = value => {
    const nextValue = typeof value === "function" ? value(backgroundColor) : value;
    onChange(nextValue);
  };

  const handleSquareClick = (): void => {
    sharedDialog.openSharedDialog({
      title: t(backgroundControlTranslationKeys.selectBackgroundColor),
      onClose: undefined,
      size: "medium",
      sharedState: {
        draftEnabled: backgroundColorEnabled,
        draftColor: backgroundColor ?? "",
        isDraftColorInvalid: false,
      } satisfies IBackgroundDialogState,
      customContent: (
        <ImageBackgroundDialogContent
          strictIsolationGuaranteed={strictIsolationGuaranteed}
          presetList={defaultPresets}
        />
      ),
      className: "image-background-dialog",
      customFooter: ({ id }) => (
        <ImageBackgroundDialogFooter
          dialogId={id}
          backgroundColor={backgroundColor}
          backgroundColorEnabled={backgroundColorEnabled}
          setBackgroundColor={setBackgroundColor}
          setBackgroundColorEnabled={setBackgroundColorEnabled}
        />
      ),
      customDismissBehavior: { closeIcon: true, escape: false, outsideClick: false },
    });
  };

  const squareButtonColor = backgroundColor ? backgroundColor : "#20597e99";

  return (
    <Fragment>
      {/* visible small color square */}
      <TooltipWrapper
        targetRef=".select-background-color-of-image"
        position="top"
        content={disabled ? null : t(backgroundControlTranslationKeys.selectBackgroundColor)}
        disabled={disabled}
      />

      <div
        data-testid="image-background-color-button"
        className={classNames("select-background-color-of-image color-square", { disabled: disabled })}
        role="button"
        aria-pressed={backgroundColorEnabled}
        aria-label={t(backgroundControlTranslationKeys.selectBackgroundColor)}
        tabIndex={0}
        onClick={() => {
          disabled ? null : handleSquareClick();
        }}
        onKeyDown={e => {
          if ((e.key === "Enter" || e.key === " ") && !disabled) {
            e.preventDefault();
            handleSquareClick();
          }
        }}
        style={{
          background: squareButtonColor,
        }}
      />
    </Fragment>
  );
};
